import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./ProgramSetup.module.css";

/**
 * Formulário inline de configuração do programa de fidelidade.
 *
 * Modos:
 * - "create": render dentro do empty-state já transformado em formulário
 *   (controlado pelo pai — quando programa não existe e usuário clicou
 *   em "Criar meu programa").
 * - "edit": render quando programa já existe; permite editar os 3 campos
 *   + toggle de ativação.
 *
 * Limites (espelham a validação Zod do backend em `loyaltyRoutes.ts`):
 * - requiredVisits: inteiro em [1, 50]
 * - rewardDescription: string >= 3 chars
 *
 * Comportamento do toggle "Programa ativo":
 * - Em modo "edit" (programa já existe): clicar o toggle persiste na hora via
 *   `onToggleActive(nextValue)` — o pai faz PATCH inline (upsert com os valores
 *   PERSISTIDOS + isActive flipado), com optimistic update, toast e rollback.
 *   NÃO arrasta junto os edits locais não-salvos de visits/reward.
 * - Em modo "create" (programa ainda não existe): o toggle é só estado local e
 *   é persistido na criação inicial via "Salvar programa" (não há o que PATCHar).
 *
 * "Salvar programa" continua salvando os campos do formulário (visits/reward)
 * e é independente da ação do toggle.
 */

const MIN_VISITS = 1;
const MAX_VISITS = 50;
const MIN_REWARD_LEN = 3;

export default function ProgramSetup({
  program = null,
  onSave,
  onToggleActive,
  saving = false,
  togglingActive = false,
}) {
  const uid = useId();
  const isEditing = Boolean(program);

  // Estado controlado — inicializa a partir do programa existente (edit)
  // ou usa defaults sensatos (create).
  const [requiredVisits, setRequiredVisits] = useState(
    program?.requiredVisits ?? 10,
  );
  const [rewardDescription, setRewardDescription] = useState(
    program?.rewardDescription ?? "",
  );
  const [isActive, setIsActive] = useState(program?.isActive ?? true);

  // Validação inline
  const [touched, setTouched] = useState({ visits: false, reward: false });
  const [errors, setErrors] = useState({ visits: null, reward: null });

  // Guarda a última versão PERSISTIDA dos campos de texto que já sincronizamos
  // pro estado local. Serve pra distinguir "o programa mudou de verdade no
  // backend (criação ou save explícito)" de "o pai só flipou isActive via PATCH
  // inline do toggle". No segundo caso NÃO podemos resetar visits/reward, senão
  // clobbaríamos o que o usuário está digitando.
  const syncedTextRef = useRef({ requiredVisits: null, rewardDescription: null });

  // ─── Sync dos campos de TEXTO (visits/reward) ────────────────────────────
  // Só re-sincroniza quando os valores persistidos no backend realmente mudam
  // (primeira carga do programa, ou após "Salvar programa"). Como o toggle
  // inline não toca nesses campos no backend, flipar isActive não dispara reset.
  // Clamp defensivo: se vier `requiredVisits` fora de [1, 50] (ex.: dado legado
  // ou migração com defaults antigos), espremer pro range válido para evitar
  // inconsistência UI/Zod no submit.
  useEffect(() => {
    if (!program) return;
    const visits = Number(program.requiredVisits) || 10;
    const clamped = Math.max(MIN_VISITS, Math.min(MAX_VISITS, visits));
    const reward = program.rewardDescription ?? "";
    const prev = syncedTextRef.current;
    if (prev.requiredVisits === visits && prev.rewardDescription === reward) {
      // Mesmos valores persistidos de texto → nada novo do backend. Não
      // resetar (evita apagar edits em andamento durante toggle inline).
      return;
    }
    syncedTextRef.current = { requiredVisits: visits, rewardDescription: reward };
    setRequiredVisits(clamped);
    if (clamped !== visits) {
      // eslint-disable-next-line no-console
      console.warn(
        `[ProgramSetup] requiredVisits=${visits} clamped to ${clamped}`,
      );
    }
    setRewardDescription(reward);
  }, [program]);

  // ─── Sync do estado ATIVO (isActive) ─────────────────────────────────────
  // Efeito separado que re-roda quando `program` muda, atualizando apenas o
  // toggle `isActive`. Mantém o toggle em sincronia com a resposta do PATCH
  // inline (e com o estado optimistic do pai) sem mexer nos campos de texto.
  useEffect(() => {
    if (!program) return;
    setIsActive(Boolean(program.isActive));
  }, [program]);

  const validate = (visitsVal, rewardVal) => {
    const next = { visits: null, reward: null };
    const v = Number(visitsVal);
    if (!Number.isInteger(v) || v < MIN_VISITS || v > MAX_VISITS) {
      next.visits = `Informe um número inteiro entre ${MIN_VISITS} e ${MAX_VISITS}.`;
    }
    const trimmed = (rewardVal ?? "").trim();
    if (trimmed.length < MIN_REWARD_LEN) {
      next.reward = `Descreva o prêmio com pelo menos ${MIN_REWARD_LEN} caracteres.`;
    }
    return next;
  };

  const handleVisitsChange = (next) => {
    const clamped = Math.max(MIN_VISITS, Math.min(MAX_VISITS, next));
    setRequiredVisits(clamped);
    if (touched.visits) {
      setErrors((prev) => ({ ...prev, ...validate(clamped, rewardDescription) }));
    }
  };

  const handleVisitsInput = (e) => {
    // Permitir digitação livre (string), mas guardar como número quando válido.
    // Vazio temporário durante edição → trata como 0 (vai falhar validação on blur).
    const raw = e.target.value;
    if (raw === "") {
      setRequiredVisits("");
      return;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) {
      setRequiredVisits(n);
    }
  };

  const handleRewardChange = (e) => {
    const value = e.target.value;
    setRewardDescription(value);
    if (touched.reward) {
      setErrors((prev) => ({ ...prev, ...validate(requiredVisits, value) }));
    }
  };

  const previewText = useMemo(() => {
    const trimmed = rewardDescription.trim();
    const visitsLabel = Number.isInteger(Number(requiredVisits)) && Number(requiredVisits) > 0
      ? Number(requiredVisits)
      : "X";
    if (!trimmed) {
      return {
        line: `A cada ${visitsLabel} cortes, o cliente ganha:`,
        reward: "configure o prêmio acima",
        muted: true,
      };
    }
    return {
      line: `A cada ${visitsLabel} cortes, o cliente ganha:`,
      reward: trimmed,
      muted: false,
    };
  }, [requiredVisits, rewardDescription]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ visits: true, reward: true });
    const nextErrors = validate(requiredVisits, rewardDescription);
    setErrors(nextErrors);
    if (nextErrors.visits || nextErrors.reward) return;
    onSave({
      requiredVisits: Number(requiredVisits),
      rewardDescription: rewardDescription.trim(),
      isActive,
    });
  };

  // Toggle "Programa ativo":
  // - edit mode → PATCH inline imediato delegado ao pai (optimistic + rollback).
  //   Não enviamos os edits locais de visits/reward; o pai usa os valores
  //   PERSISTIDOS. Ignoramos cliques enquanto um PATCH está em flight.
  // - create mode → só estado local; persiste na criação via "Salvar programa".
  const handleToggleChange = (e) => {
    const next = e.target.checked;
    if (isEditing) {
      if (togglingActive) return;
      onToggleActive?.(next);
      return;
    }
    setIsActive(next);
  };

  const visitsInputId = `${uid}-visits`;
  const rewardInputId = `${uid}-reward`;
  const toggleInputId = `${uid}-toggle`;
  const visitsErrorId = `${uid}-visits-error`;
  const rewardErrorId = `${uid}-reward-error`;

  return (
    <form className={styles.card} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.cardTitle}>
        {isEditing ? "Configurar programa" : "Crie seu programa de fidelidade"}
      </h2>

      {isEditing && !isActive && (
        <div className={styles.inactiveBanner} role="status">
          <span className={styles.inactiveBannerIcon} aria-hidden="true">
            ⚠️
          </span>
          <span>
            Seu programa está pausado. Ative o seletor abaixo para começar a
            contar as visitas.
          </span>
        </div>
      )}

      {/* ─── Stepper de visitas ─────────────────────────────────────── */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor={visitsInputId}>
          Quantos cortes para ganhar o prêmio?
        </label>
        <div
          className={styles.stepper}
          role="group"
          aria-label="Número de cortes para ganhar o prêmio"
        >
          <button
            type="button"
            className={styles.stepperBtn}
            onClick={() => handleVisitsChange(Number(requiredVisits || 0) - 1)}
            disabled={Number(requiredVisits) <= MIN_VISITS}
            aria-label="Diminuir número de cortes"
          >
            −
          </button>
          <input
            id={visitsInputId}
            type="number"
            inputMode="numeric"
            min={MIN_VISITS}
            max={MAX_VISITS}
            step={1}
            value={requiredVisits}
            onChange={handleVisitsInput}
            onBlur={() => {
              setTouched((t) => ({ ...t, visits: true }));
              setErrors((prev) => ({
                ...prev,
                ...validate(requiredVisits, rewardDescription),
              }));
            }}
            className={styles.stepperInput}
            aria-describedby={errors.visits ? visitsErrorId : undefined}
            aria-invalid={Boolean(errors.visits)}
          />
          <button
            type="button"
            className={styles.stepperBtn}
            onClick={() => handleVisitsChange(Number(requiredVisits || 0) + 1)}
            disabled={Number(requiredVisits) >= MAX_VISITS}
            aria-label="Aumentar número de cortes"
          >
            +
          </button>
        </div>
        <p className={styles.hint}>
          Entre {MIN_VISITS} e {MAX_VISITS} cortes.
        </p>
        {errors.visits && (
          <p className={styles.errorText} id={visitsErrorId} role="alert">
            {errors.visits}
          </p>
        )}
      </div>

      {/* ─── Descrição do prêmio ────────────────────────────────────── */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor={rewardInputId}>
          Qual é o prêmio?
        </label>
        <input
          id={rewardInputId}
          type="text"
          value={rewardDescription}
          onChange={handleRewardChange}
          onBlur={() => {
            setTouched((t) => ({ ...t, reward: true }));
            setErrors((prev) => ({
              ...prev,
              ...validate(requiredVisits, rewardDescription),
            }));
          }}
          placeholder="Ex: Corte grátis, 50% de desconto, Barba grátis..."
          className={`${styles.input} ${errors.reward ? styles.inputError : ""}`.trim()}
          maxLength={120}
          autoComplete="off"
          aria-describedby={errors.reward ? rewardErrorId : undefined}
          aria-invalid={Boolean(errors.reward)}
        />
        {errors.reward && (
          <p className={styles.errorText} id={rewardErrorId} role="alert">
            {errors.reward}
          </p>
        )}
      </div>

      {/* ─── Toggle ativo ───────────────────────────────────────────── */}
      <div className={styles.field}>
        <div className={styles.toggleRow}>
          <div>
            <p className={styles.toggleLabel}>Programa ativo</p>
            <p className={styles.toggleHelp}>
              {isEditing
                ? "Ligue ou desligue na hora — a mudança é salva automaticamente."
                : "Quando ativo, o chatbot conta as visitas dos seus clientes."}
            </p>
          </div>
          <label
            className={`${styles.toggle} ${
              togglingActive ? styles.toggleLoading : ""
            }`.trim()}
            htmlFor={toggleInputId}
          >
            <input
              id={toggleInputId}
              type="checkbox"
              role="switch"
              className={styles.toggleInput}
              checked={isActive}
              onChange={handleToggleChange}
              disabled={togglingActive}
              aria-checked={isActive}
              aria-disabled={togglingActive}
            />
            <span className={styles.toggleSlider} aria-hidden="true" />
          </label>
        </div>
      </div>

      {/* ─── Preview em tempo real ──────────────────────────────────── */}
      <div className={styles.preview} aria-live="polite">
        <p className={styles.previewTitle}>🎯 Seu cartão fidelidade ficará assim</p>
        <p className={styles.previewText}>
          {previewText.line}{" "}
          <strong className={previewText.muted ? styles.previewMuted : undefined}>
            {previewText.reward}
            {!previewText.muted && "!"}
          </strong>
        </p>
        <p className={styles.previewFooter}>
          O chatbot já avisa o cliente automaticamente a cada visita. ✅
        </p>
      </div>

      <div className={styles.actions}>
        <button type="submit" className={styles.saveBtn} disabled={saving}>
          {saving ? "Salvando..." : "Salvar programa"}
        </button>
      </div>
    </form>
  );
}

ProgramSetup.propTypes = {
  program: PropTypes.shape({
    requiredVisits: PropTypes.number,
    rewardDescription: PropTypes.string,
    isActive: PropTypes.bool,
  }),
  onSave: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func,
  saving: PropTypes.bool,
  togglingActive: PropTypes.bool,
};
